/**
 * useChromeStore — theme, locale, newsletter modal, and force-update flag.
 *
 * Part of the useDisplayStore split (architecture review item 5). Owns
 * theme tokens (delegated to `themeManager`), i18n locale setup, and the
 * cross-page modal flags. Layout/sidebar state lives in `useLayoutStore`;
 * chart synchronisation lives in `useChartSyncStore`.
 */

import i18n from '@/i18n';
import { flushAnalytics, trackAnalyticsEvent } from '@/composables/useAnalytics';
import { LOCALE_KEY } from '@/storage/keys';
import {
  applyTheme,
  defaultThemeMode,
  defaultThemePreset,
  getStoredTheme,
  setThemeMode as applyThemeMode,
  setThemePreset as applyThemePreset,
  toggleThemeMode as applyThemeModeToggle,
} from '@/styles/theme/themeManager';
import { defineStore } from 'pinia';

function readLocale() {
  if (typeof localStorage === 'undefined') return null;
  const legacy = localStorage.getItem('locale');
  if (legacy !== null) {
    if (localStorage.getItem(LOCALE_KEY) === null) {
      localStorage.setItem(LOCALE_KEY, legacy);
    }
    localStorage.removeItem('locale');
  }
  const value = localStorage.getItem(LOCALE_KEY);
  return value && value !== 'undefined' ? value : null;
}

// Single emit point for theme changes, deduped by the final effective
// (preset, mode) signature so one user action emits at most one event even when
// it routes through multiple setters, and so re-applying the same theme is a no-op.
let lastThemeSignature = null;

function emitThemeChanged(applied) {
  const signature = `${applied.preset}|${applied.mode}`;
  if (signature === lastThemeSignature) {
    return;
  }
  lastThemeSignature = signature;
  trackAnalyticsEvent('preference.theme_changed', {
    resource_type: 'preference',
    resource_id: 'theme',
    properties: {
      theme: applied.preset,
      theme_preset: applied.preset,
      theme_mode: applied.mode,
    },
  });
}

export const useChromeStore = defineStore('chrome', {
  state: () => ({
    /* Dark mode / theme */
    darkMode: false,
    themePreset: defaultThemePreset,
    themeMode: defaultThemeMode,
    /* Modals */
    isNewsletterModalActive: false,
    /* Misc cross-page UI flags */
    isFullscreenLoaderForced: false,
    forcedUpdate: false,
    updatesStatus: false,
  }),

  actions: {
    setDarkMode(value) {
      this.darkMode = value;
    },

    newsletterModalToggle(payload) {
      this.isNewsletterModalActive = payload;
    },

    setFullscreenLoaderForced(payload) {
      this.isFullscreenLoaderForced = Boolean(payload);
    },

    forceUpdate(payload) {
      this.forcedUpdate = payload !== undefined ? payload : !this.forcedUpdate;
    },

    defaultLocale() {
      const stored = readLocale();
      if (stored) {
        i18n.global.locale.value = stored;
      } else {
        i18n.global.locale.value = 'nl';
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(LOCALE_KEY, i18n.global.locale.value);
        }
      }
    },

    defaultDarkMode() {
      return this.defaultTheme();
    },

    defaultTheme() {
      const stored = getStoredTheme();
      const applied = applyTheme({
        preset: stored.preset || this.themePreset,
        mode: stored.mode || this.themeMode,
      });
      this.themePreset = applied.preset;
      this.themeMode = applied.mode;
      this.darkMode = applied.mode === 'dark';
    },

    setLocale(locale) {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(LOCALE_KEY, locale);
      }
      i18n.global.locale.value = locale;
      if (typeof document !== 'undefined') {
        document.documentElement.lang = locale;
      }
      trackAnalyticsEvent('preference.locale_changed', {
        resource_type: 'preference',
        resource_id: 'locale',
        properties: { locale },
      });
      flushAnalytics({ beacon: true });
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    },

    setThemePreset(preset) {
      const applied = applyThemePreset(preset || this.themePreset);
      this.themePreset = applied.preset;
      this.themeMode = applied.mode;
      this.darkMode = applied.mode === 'dark';
      emitThemeChanged(applied);
    },

    setThemeMode(mode) {
      const applied = applyThemeMode(mode || this.themeMode);
      this.themePreset = applied.preset;
      this.themeMode = applied.mode;
      this.darkMode = applied.mode === 'dark';
      emitThemeChanged(applied);
    },

    toggleThemeMode() {
      const applied = applyThemeModeToggle();
      this.themePreset = applied.preset;
      this.themeMode = applied.mode;
      this.darkMode = applied.mode === 'dark';
      emitThemeChanged(applied);
    },

  },
});
